import { useEffect } from 'react';
import { useUpdate } from 'react-use';

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;
// const MONTH = DAY * 30;
// const YEAR = DAY * 365;

const getUnboundPeriod = (seconds: number) => {
  if (seconds < MINUTE) {
    return 1000;
  } else if (seconds < HOUR) {
    return 1000 * MINUTE;
  } else if (seconds < DAY) {
    return 1000 * HOUR;
  } else {
    return 1000 * WEEK;
  }
};

export interface IUseTimeAgoOptions {
  /** The Date to display. An actual Date object or something that can be fed to new Date */
  date: Date;
  /** A function to decide how to format the date.
   * This hook basically acting like a glorified setInterval for you.
   */
  formatter: (date: Date) => string;
  /** minimum amount of time in seconds between re-renders */
  minPeriod?: number;
  /** Maximum time between re-renders in seconds. The component should update at least once every `x` seconds */
  maxPeriod?: number;
}

/**
 * This hook basically acting like a glorified setInterval, but it's optimized for less re-renders.
 */
export const useTimeAgo = ({ date, minPeriod, maxPeriod, formatter }: IUseTimeAgoOptions) => {
  const update = useUpdate();

  useEffect(() => {
    let timeoutId;
    const tick = (refresh?: boolean): void => {
      const then = date.valueOf();
      const timeNow = Date.now();
      const seconds = Math.round(Math.abs(timeNow - then) / 1000);
      const unboundPeriod = getUnboundPeriod(seconds);
      const period = Math.min(Math.max(unboundPeriod, minPeriod * 1000), maxPeriod * 1000);

      if (period) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(tick, period);
      }

      if (!refresh) {
        update();
      }
    };

    tick(true);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [date, maxPeriod, minPeriod, update]);

  return formatter(date);
};
